<template>
  <div class="d-flex justify-center align-center wrap flex-column success-page">
    <img src="../../assets/img/confetti.png" class="sp-confetti" />
    <div class="ty-d2d">
      Thank you for booking with the <br/>
      {{ brandLabel }} Flights!
    </div>
    <div class="ticket-copy">
      <span v-if="!isPending">We've sent you the ticket copy to your email.</span>
      <span v-else>You will receive a booking confirmation notifications once your trip is confirmed with the airlines.</span>
    </div>

    <div class="trip-details-box">
      <div class="td-head">
        <div class="td-field">
          Passenger Details:
          <div v-for="(p, i) in pax.adult" :key="`a${i}`">
            <b>{{ `${p['title' + i]} ${p['firstName' + i]} ${p['middleName' + i]} ${p['lastName' + i]}` }}</b>
          </div>
          <div v-for="(p, i) in pax.children" :key="`c${i}`">
            <b>{{ `${p['title' + i]} ${p['firstName' + i]} ${p['middleName' + i]} ${p['lastName' + i]}` }}</b>
          </div>
          <div v-for="(p, i) in pax.infants" :key="`i${i}`">
            <b>{{ `${p['title' + i]} ${p['firstName' + i]} ${p['middleName' + i]} ${p['lastName' + i]}` }}</b>
          </div>
        </div>
        <div v-if="!isPending" class="td-field">
          Ticket ID:
          <b>{{booking.bookingRef.leg1.bookingId}}</b><b v-if="isReturning && settings.customFlight">, {{booking.bookingRef.leg2.bookingId}}</b>
        </div>
        <div v-if="!isPending" class="td-field">
          Ticket Number:
          <b>{{booking.bookingRef.leg1.pnr}}</b><b v-if="isReturning && settings.customFlight">, {{booking.bookingRef.leg2.pnr}}</b>
        </div>
      </div>

      <div class="td-body d-flex align-start">
        <i class="fas fa-plane td-icon"></i>
        <div class="td-b-content">
          <div class="td-b-label">
            <span v-if="isReturning">Roundtrip</span> 
            <span v-else>One Way</span> 
            Flight
          </div>
          <div class="td-flight d-flex align-center">
            {{ leg1Selected.flight[0].origin.AirportName }} ({{ leg1Selected.flight[0].origin.AirportCode }})
            <i class="fas fa-plane"></i>
            {{ leg1Selected.flight[leg1Selected.flight.length - 1].destination.AirportName }} ({{ leg1Selected.flight[leg1Selected.flight.length - 1].destination.AirportCode }})
          </div>
          <div class="td-airline d-flex align-center">
            <!-- <img src="" /> -->
            <div>{{ leg1Selected.flightName }}</div>
            <span>{{ dateFormat(leg1Selected.flight[0].origin.DateTime) }}</span>
          </div>
          <div class="td-airline d-flex align-center">
            {{ timeFormat(leg1Selected.flight[0].origin.DateTime) }}
            -
            {{ timeFormat(leg1Selected.flight[leg1Selected.flight.length - 1].destination.DateTime) }}
            ({{ timeDiff(leg1Selected.flight[0].origin.DateTime, leg1Selected.flight[leg1Selected.flight.length - 1].destination.DateTime) }}, {{ countStop(leg1Selected.flight) }})
          </div>
          <div class="td-a-rt" v-if="isReturning">
            <div class="td-flight d-flex align-center">
              {{ selectedLeg2Flight[0].origin.AirportName }} ({{ selectedLeg2Flight[0].origin.AirportCode }})
              <i class="fas fa-plane"></i>
              {{ selectedLeg2Flight[selectedLeg2Flight.length - 1].destination.AirportName }} ({{ selectedLeg2Flight[selectedLeg2Flight.length - 1].destination.AirportCode }})
            </div>
            <div class="td-airline d-flex align-center">
              <!-- <img src="" /> -->
               <div>{{ settings.customFlight ? leg2Selected.flightName : selectedLeg2Flight[0].airline.name }}</div>
              <span>{{ dateFormat(selectedLeg2Flight[0].origin.DateTime) }}</span>
            </div>
            <div class="td-airline d-flex align-center">
              {{ timeFormat(selectedLeg2Flight[0].origin.DateTime) }}
              -
              {{ timeFormat(selectedLeg2Flight[selectedLeg2Flight.length - 1].destination.DateTime) }}
              ({{ timeDiff(selectedLeg2Flight[0].origin.DateTime, selectedLeg2Flight[selectedLeg2Flight.length - 1].destination.DateTime) }}, {{ countStop(selectedLeg2Flight) }})
            </div>
          </div>
        </div>
      </div>

      <div class="td-body d-flex align-start" v-if="includedLandTranspo.length">
        <i class="fas fa-car-side td-icon"></i>
        <div class="td-b-content">
          <div class="td-b-label">Ground Transportation</div>

          <div v-if="!locations.isAirportPickup">
            <!-- GROUND: LEG1 Pickup -->
            <div class="td-leg d-flex align-center" v-if="includedLandTranspo.indexOf(0) > -1">
              Leg 1 - Depart
              <div class="td-status">Upcoming</div>
            </div>
            <div class="td-field" v-if="includedLandTranspo.indexOf(0) > -1">
              <div class="td-f-label">Pickup Location</div>
              <div class="td-f-value">{{ locations.pickup.title }}</div>
            </div>
            <div class="td-field" v-if="includedLandTranspo.indexOf(0) > -1">
              <div class="td-f-label">Dropoff Location</div>
              <div class="td-f-value">{{ locations.pickupAirport.name }} ({{locations.pickupAirport.code}})</div>
            </div>
            <div class="td-date d-flex align-start" v-if="includedLandTranspo.indexOf(0) > -1">
              <div class="td-field">
                <div class="td-f-label">Fleet Type</div>
                <div class="td-f-value">{{ cabinLabel(findById('leg1', 'pickup').name) }}</div>
              </div>
              <div class="td-field">
                <div class="td-f-label">Date</div>
                <div class="td-f-value">{{ dateFormat2(locations.pickupDetails.pickupTime.leg1, true) }}</div>
              </div>
              <div class="td-field">
                <div class="td-f-label">Time</div>
                <div class="td-f-value">{{ timeFormat(locations.pickupDetails.pickupTime.leg1, true) }}</div>
              </div>
            </div>

            <!-- GROUND: LEG1 Dropoff -->
            <div class="td-leg d-flex align-center" v-if="includedLandTranspo.indexOf(1) > -1">
              Leg 1 - Arrive
              <div class="td-status">Upcoming</div>
            </div>
            <div class="td-field" v-if="includedLandTranspo.indexOf(1) > -1">
              <div class="td-f-label">Pickup Location</div>
              <div class="td-f-value">{{ locations.dropoffAirport.name }} ({{locations.dropoffAirport.code}})</div>
            </div>
            <div class="td-field" v-if="includedLandTranspo.indexOf(1) > -1">
              <div class="td-f-label">Dropoff Location</div>
              <div class="td-f-value">{{ locations.dropoff.title }}</div>
            </div>
            <div class="td-date d-flex align-start" v-if="includedLandTranspo.indexOf(1) > -1">
              <div class="td-field">
                <div class="td-f-label">Fleet Type</div>
                <div class="td-f-value">{{ cabinLabel(findById('leg1', 'dropoff').name) }}</div>
              </div>
              <div class="td-field">
                <div class="td-f-label">Date</div>
                <div class="td-f-value">{{ dateFormat2(locations.dropoffDetails.pickupTime.leg1, true) }}</div>
              </div>
              <div class="td-field">
                <div class="td-f-label">Time</div>
                <div class="td-f-value">{{ timeFormat(locations.dropoffDetails.pickupTime.leg1, true) }}</div>
              </div>
            </div>
          </div>

          <hr class="ground-hr" v-if="isReturning && !locations.isAirportPickup && !locations.isAirportDropoff && ((includedLandTranspo.includes(0) || includedLandTranspo.includes(1)) && (includedLandTranspo.includes(2) || includedLandTranspo.includes(3)))" />

          <div v-if="!locations.isAirportDropoff">
            <!-- GROUND: LEG2 Pickup -->
            <div class="td-leg d-flex align-center" v-if="includedLandTranspo.indexOf(2) > -1 && isReturning">
              Leg 2 - Depart
              <div class="td-status">Upcoming</div>
            </div>
            <div class="td-field" v-if="includedLandTranspo.indexOf(2) > -1 && isReturning">
              <div class="td-f-label">Pickup Location</div>
              <div class="td-f-value">{{ locations.dropoff.title }}</div>
            </div>
            <div class="td-field" v-if="includedLandTranspo.indexOf(2) > -1 && isReturning">
              <div class="td-f-label">Dropoff Location</div>
              <div class="td-f-value">{{ locations.dropoffAirport.name }} ({{locations.dropoffAirport.code}})</div>
            </div>
            <div class="td-date d-flex align-start" v-if="includedLandTranspo.indexOf(2) > -1 && isReturning">
              <div class="td-field">
                <div class="td-f-label">Fleet Type</div>
                <div class="td-f-value">{{ cabinLabel(findById('leg2', 'pickup').name) }}</div>
              </div>
              <div class="td-field">
                <div class="td-f-label">Date</div>
                <div class="td-f-value">{{ dateFormat2(locations.pickupDetails.pickupTime.leg2, true) }}</div>
              </div>
              <div class="td-field">
                <div class="td-f-label">Time</div>
                <div class="td-f-value">{{ timeFormat(locations.pickupDetails.pickupTime.leg2, true) }}</div>
              </div>
            </div>

            <!-- GROUND: LEG2 Dropoff -->
            <div class="td-leg d-flex align-center" v-if="includedLandTranspo.indexOf(3) > -1 && isReturning">
              Leg 2 - Arrive
              <div class="td-status">Upcoming</div>
            </div>
            <div class="td-field" v-if="includedLandTranspo.indexOf(3) > -1 && isReturning">
              <div class="td-f-label">Pickup Location</div>
              <div class="td-f-value">{{ locations.pickupAirport.name }} ({{locations.pickupAirport.code}})</div>
            </div>
            <div class="td-field" v-if="includedLandTranspo.indexOf(3) > -1 && isReturning">
              <div class="td-f-label">Dropoff Location</div>
              <div class="td-f-value">{{ locations.pickup.title }}</div>
            </div>
            <div class="td-date d-flex align-start" v-if="includedLandTranspo.indexOf(3) > -1 && isReturning">
              <div class="td-field">
                <div class="td-f-label">Fleet Type</div>
                <div class="td-f-value">{{ cabinLabel(findById('leg2', 'dropoff').name) }}</div>
              </div>
              <div class="td-field">
                <div class="td-f-label">Date</div>
                <div class="td-f-value">{{ dateFormat2(locations.dropoffDetails.pickupTime.leg2, true) }}</div>
              </div>
              <div class="td-field">
                <div class="td-f-label">Time</div>
                <div class="td-f-value">{{ timeFormat(locations.dropoffDetails.pickupTime.leg2, true) }}</div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

    <div class="success-box" v-if="false">
      <div class="sb-padder">
        <i class="fas fa-clock fa-5x" v-if="isPending"></i>
        <i class="fas fa-check-circle fa-5x" v-else></i>
        <div class="text1" v-if="isPending">Booking Pending!</div>
        <div class="text1" v-else>Booking Success!</div>
        <div class="text2">
          Thank you for booking with us!
          <div v-if="isPending">You will receive a booking confirmation notifications once your trip is confirmed with the airlines.</div>
        </div>
        
        <div class="booking-info" v-if="!isPending && !isReturning">
          <div>Booking ID</div>
          <b>{{booking.bookingRef.leg1.bookingId}}</b>
        </div>
        <div class="booking-info" v-if="booking.bookingRef.leg1.pnr && !isPending && !isReturning">
          <div>Airline PNR</div>
          <b>
            {{booking.bookingRef.leg1.pnr}}
          </b>
        </div>
      </div>

      <div class="dual-book d-flex" v-if="isReturning">
        <div>
          <div class="margin-b-s">
            <label class="label-type">FIRST TRIP</label>
            <label>Booking ID</label>
            <b>{{booking.bookingRef.leg1.bookingId}}</b>
          </div>
          <div>
            <label>Airline PNR</label>
            <b>{{booking.bookingRef.leg1.pnr}}</b>
          </div>
        </div>
        <div>
          <div class="margin-b-s">
            <label class="label-type">RETURN TRIP</label>
            <label>Booking ID</label>
            <b>{{booking.bookingRef.leg2.bookingId}}</b>
          </div>
          <div>
            <label>Airline PNR</label>
            <b>{{booking.bookingRef.leg2.pnr}}</b>
          </div>
        </div>
      </div>

      <div class="sb-padder">
        <div class="booking-info margin-b-z">
          <div>Confirmation <span v-if="isPending">will be</span> sent to</div>
          <b>{{user.email}}</b>
        </div>
      </div>
    </div>
    <div class="home-link">
      <a href="/">Back to Home page</a>
    </div>
  </div>
</template>

<script>
/* global CONFIG */
import moment from 'moment-timezone'
import xcurrency from '@/modules/api/currency'

export default {
  name: 'success',
  data () {
    return {
      isPending: false
    }
  },
  computed: {
    user () { return this.$store.state.user },
    booking () { return this.$store.state.booking },
    locations () { return this.booking.locations },
    airlines () { return this.booking.airlines },
    filters () { return this.booking.filters },
    travellers () { return this.filters.travellers },
    isReturning () { return this.filters.departure.isReturning },
    selected () { return this.booking.selected },
    leg1Selected () { return this.selected.leg1 },
    leg2Selected () { return this.selected.leg2 },
    selectedLeg2Flight () { return this.settings.customFlight ? this.leg2Selected.flight : this.leg1Selected.returnFlight },
    landQuote () { return this.booking.landQuote },
    settings () { return this.booking.settings },
    gtag () { return this.$store.state.gtag },
    currencyStore () { return this.$store.state.other.currency },
    pax () {
      // let pax = []
      // this.travellers.info.adult.forEach(adult => { pax.push(adult) })
      // this.travellers.info.children.forEach(children => { pax.push(children) })
      // this.travellers.info.infants.forEach(infants => { pax.push(infants) })
      // if (this.travellers.adult) return this.travellers.info.adult[0]
      // if (this.travellers.children) return this.travellers.info.children[0]
      // if (this.travellers.infants) return this.travellers.info.infants[0]
      return this.travellers.info
    },
    includedLandTranspo () { return this.booking.includedLandTranspo },
    brandLabel () {
      let brand = Object.values(CONFIG.Brands).find(x => x.id === this.$store.state.brandId)
      return brand ? brand.label : 'Door2Door'
    }
  },
  methods: {
    gtagPurchaseEvent (authData, leg) {
      if (!window.gtag) return

      let currency = 'USD'
      let total = authData.fare.flight + authData.fare.rsvp
      let discount = authData.fare.credits
      if (authData.currency) {
        currency = authData.currency.currency
        total = authData.currency.flight + authData.currency.rsvp
        discount = authData.currency.credits
      }
      let tax = authData.price.UpdateFareQuote.FareQuoteDetails.JourneyList.Price.PriceBreakup.Tax
      if (currency !== authData.price.UpdateFareQuote.FareQuoteDetails.JourneyList.Price.Currency) {
        let tmpTax = this.reconvertCurrency(tax, authData.price.UpdateFareQuote.FareQuoteDetails.JourneyList.Price.Currency)
        if (authData.price.UpdateFareQuote.FareQuoteDetails.JourneyList.Price.Currency !== 'USD') {
          tmpTax = this.applyCurrency(tax, false)
        }
        tax = tmpTax
      }
      let purchaseData = {
        transaction_id: this.booking.bookingRef[leg].bookingId,
        affiliation: 'Flight Booking Website',
        currency: currency,
        value: total,
        discount: discount,
        shipping: 0,
        tax: tax || 0,
        items: []
      }
      authData.price.UpdateFareQuote.FareQuoteDetails.JourneyList.FlightDetails.Details.forEach((flight, i) => {
        purchaseData.items.push({
          index: i + 1,
          item_brand: flight[0].OperatorName,
          item_category: 'International Flight',
          item_id: `${flight[0].OperatorCode}${flight[0].FlightNumber}`,
          item_list_id: 'flight_search_results',
          item_list_name: 'Flight Search Results',
          item_name: `Flight from ${flight[0].Origin.AirportCode} to ${flight[flight.length - 1].Destination.AirportCode}`,
          item_variant: this.airlines.selectedCabin,
          location_id: `${flight[0].Origin.AirportCode}-${flight[flight.length - 1].Destination.AirportCode}`
        })
      })
      window.gtag('event', 'purchase', purchaseData)
    },
    applyCurrency (x, useSymbol = true) {
      x = parseFloat(x)
      x = x * this.currencyStore.value
      if (!useSymbol) {
        return parseFloat(x.toFixed(2))
      }
      return xcurrency.format({
        code: this.currency.code,
        val: x.toFixed(2)
      }, useSymbol)
    },
    reconvertCurrency (x, code) {
      let currRate = this.currencyStore.list[code]
      x = parseFloat(x.toFixed(2))
      x = parseFloat((x / currRate).toFixed(2))
      return x
    },
    dateFormat (x, isFormated = false, eta = null) {
      if (!x) return ''
      let format = isFormated ? 'MM/DD/YYYY hh:mmA' : 'YYYY-MM-DD HH:mm'
      let date = moment(x, format)
      if (eta) date.add(eta, 'minutes')
      return date.format('ddd, MMM DD')
    },
    dateFormat2 (x, isFormated = false, eta = null) {
      if (!x) return ''
      let format = isFormated ? 'MM/DD/YYYY hh:mmA' : 'YYYY-MM-DD HH:mm'
      let date = moment(x, format)
      if (eta) date.add(eta, 'minutes')
      return date.format('MMM DD, YYYY')
    },
    timeFormat (x, isFormated = false, eta = null) {
      if (!x) return ''
      let format = isFormated ? 'MM/DD/YYYY hh:mmA' : 'YYYY-MM-DD HH:mm'
      let date = moment(x, format)
      if (eta) date.add(eta, 'minutes')
      return date.format('h:mma')
    },
    timeDiff (x, y) {
      if (!x || !y) return ''
      x = moment(x, 'YYYY-MM-DD HH:mm')
      y = moment(y, 'YYYY-MM-DD HH:mm')
      let duration = moment.duration(y.diff(x))
      return `${duration._data.hours}h ${duration._data.minutes}m`
    },
    countStop (data) {
      if (data.length === 1) return 'Direct Flight'
      return `${data.length - 1} Stop${data.length > 2 ? 's' : ''}`
    },
    findById (leg, type) {
      let categories = this.landQuote[`${leg}${type === 'pickup' ? 'Depart' : 'Arrive'}`].categorized
      // let selectedID = this.selected[leg][type === 'pickup' ? 'ground1' : 'ground2']
      let selectedID = this.booking.selectedGround[leg][type]
      for (const category in categories) {
        const foundItem = categories[category].find(item => item.id === selectedID)
        if (foundItem) return foundItem
      }
      return {
        name: '',
        img: ''
      }
    },
    cabinLabel (x) {
      if (x === 'PremiumEconomy') return 'Premium Economy'
      return x
    }
  },
  mounted () {
    this.isPending = this.booking.flightPending
    if (!this.booking.bookingRef.leg1.bookingId) {
      this.$router.push('/search')
      return
    }
    this.$store.commit('reservation/setD2DData', {flightPending: false})

    if (window.gtag) {
      let id = this.booking.bookingRef.d2dIdLeg1
      if (this.isReturning) id = `${id}-${this.booking.bookingRef.d2dIdLeg2}`
      window.gtag('event', 'conversion_event_book_appointment', { 'send_to': 'AW-11473169859/ltp2CLnrnZYZEMPb6t4q', 'transaction_id': id });

      this.gtagPurchaseEvent(this.gtag.auth1, 'leg1')
      if (this.isReturning && this.gtag.auth2) this.gtagPurchaseEvent(this.gtag.auth2, 'leg2')

      let self = this
      setTimeout(() => {
        self.$store.commit('gtag/setGtagData', {
          flight1: {},
          flight2: {},
          auth1: {},
          auth2: {}
        })
      }, 2000)
    }
  }
}
</script>

<style lang="scss" scoped>
.wrap{
  width: 100%;
  min-height: calc(100vh - 20px);
}
.sp-confetti{
  width: 100px;
  transform: rotate(-15deg);
}
.ty-d2d{
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
  text-align:center;
  letter-spacing: .8px;
  margin: 10px 0;
}
.ticket-copy{
  font-size:18px;
  letter-spacing: .6px;
  margin-bottom: 30px;
  color: #717e8b;
  width: 500px;
  text-align: center;
}

.trip-details-box{
  border: 1px solid #B3B3B5;
  border-radius: 8px;
  background: #fff;
  width: 500px;
}
.td-head{
  padding: 20px;
  letter-spacing: .6px;

  .td-field{
    margin-bottom: 10px;
  }
}
.td-body{
  padding: 30px 15px;
  gap: 15px;
  border-top: 1px dashed #6D6D6D;
}
.td-icon{
  font-size: 20px;
}
.td-b-content{
  width: calc(100% - 50px);
}
.td-b-label{
  font-size: 24px;
  line-height: 1;
  font-weight: 600;
  letter-spacing: .6px;
  margin-bottom: 25px;
}
.td-flight, .td-leg{
  font-weight: 600;
  letter-spacing: .6px;
  line-height: 1;
  font-size: 18px;
  gap: 15px;
  margin-bottom: 10px;
}
.td-airline{
  font-size: 14px;
  color: #5E6E7F;
  letter-spacing: .5px;
  div{
    text-transform: capitalize;
    display: inline-block;
  }
  span{
    display: inline-block;
    margin-left: 15px;
  }
}
.td-a-rt{
  margin: 30px 0 10px;
}
.td-leg{
  margin-bottom: 20px;
}
.td-status{
  background: #ecae20;
  font-size: 13px;
  letter-spacing: .6px;
  font-weight: 500;
  padding: 5px 10px;
  color: #fff;
  line-height: 1;
  border-radius: 12px;
  display: inline-block
}
.td-field{
  margin-bottom: 20px;
  letter-spacing: .6px;
}
.td-f-label{
  font-size: 12px;
  line-height: 1;
  color: #5E6E7F;
  margin-bottom: 3px;
}
.td-f-value{
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
}
.td-date{
  gap: 40px;
  margin-bottom: 35px;
}

.ground-hr{
  border-bottom: none;
  border-left: none;
  border-right: none;
  border-top: 1px solid #ccc;
  margin-bottom: 45px;
}

.success-box{
  text-align: center;
  height: 100%;
  width: 100%;
  // max-height: 400px;
  max-width: 460px;
  border: 1px solid #ccc;
  border-radius: 20px;
  background: #fff;
  box-shadow: 4px 4px 6px rgba(0,0,0, .1);
}
.sb-padder{
  padding: 20px;
}
.fa-check-circle{
  color: #4eba5e;
  margin-bottom: 10px;
}
.fa-clock{
  color: #dadd19;
  margin-bottom: 10px;
}
.text1{
  font-size: 28px;
  line-height: 1;
  font-weight: 600;
  margin-bottom: 5px;
}
.text2{
  margin-bottom: 20px;
}

.booking-info{
  line-height: 1;
  margin-bottom: 20px;
  padding:0 15px;
  div{
    color: #777;
    margin-bottom: 5px;
  }
  b{
    font-size: 21px;
    overflow-wrap: break-word;
  }

  &.margin-b-z{
    margin-bottom: 0;
  }
}

.dual-book{
  background: #f8f8f8;
  padding: 20px;
  border-top: 2px dashed #ddd;
  border-bottom: 2px dashed #ddd;
  margin-bottom: 10px;
  justify-content: space-around;

  .label-type{
    margin-bottom: 5px;
    color: #ff9c09;
    font-weight: 600;
  }
  label{
    display: block;
    color: #888;
    line-height: 1;
    font-size: 14px!important;
    letter-spacing: .7px;
  }
  b{
    display: block;
    letter-spacing: .6px;
  }
}

.home-link{
  margin: 30px 0;
  font-weight: 600;
  letter-spacing: 1px;

  a{
    color: #cb8112;
  }
}
</style>


// WEBPACK FOOTER //
// Success.vue?e5be7e34